import React, { useMemo } from 'react';
import { useI18n } from '@locales/client';
import { Link } from '@/shared/ui/link';
import { Condition } from '@/shared/ui/condition';
import { HeadingBoldH5 } from '@/shared/ui/typography2';
import { Box } from '@/shared/ui/box';
import {
  Section,
  SectionBlocks,
  SectionViewAll,
  Sections,
  SubSection,
} from './styles';
import { useGameSections } from '@/entities/game/model/hooks';
import { ConditionalWrapperGame } from '@/entities/game/ui/сonditional-wrapper-game';

export type GameType = {
  imageUrl?: string;
  mobileLandscape?: boolean;
  title: string;
  providerName: string;
  wishlistToggleSlot: React.ReactNode;
  status?: string;
  id: string;
  // openNotAlowedModal: () => void;
  hasScroll?: boolean;
  denyBonus?: boolean;
  providerId: string;
};

type GameSectionProps = {
  games: GameType[];
  title: string;
  // openNotAlowedModal: () => void;
  rows: number;
  mobileLandscape: boolean;
  hasScroll: boolean;
  isGames: boolean;
  onGameClick: (game: GameType) => void;
  viewCount?: boolean;
};

export const GameSection = ({
  games,
  title,
  // openNotAlowedModal,
  rows,
  mobileLandscape,
  hasScroll,
  isGames,
  onGameClick,
  viewCount,
}: GameSectionProps) => {
  const t = useI18n();
  const { gamesData } = useGameSections();

  const chunkedGames = useMemo(() => {
    return Array.from({ length: rows }, (_, rowIndex) =>
      games.slice(0, 20).filter((_, i) => i % rows === rowIndex)
    );
  }, [games, rows]);

  const viewAllUrl = useMemo(() => {
    return `/games${isGames ? `?filter=${title}` : ''}`;
  }, [isGames, title]);

  const count = useMemo(() => {
    if (isGames) {
      return games?.length;
    }
    return gamesData?.length;
  }, [isGames, gamesData]);

  const displayedTitle = useMemo(() => {
    const translated = t(`common.filter.category.${title}`);
    return translated.includes('common.filter.category.') ? title : translated;
  }, [t, title]);

  const renderGame = (
    game: GameType,
    index?: number,
    sectionIndex?: number
  ) => {
    return (
      <ConditionalWrapperGame
        key={`game-${sectionIndex}-${game.id}`}
        game={game}
        handleClickGame={onGameClick}
        hasScroll={hasScroll}
      />
    );
  };

  return (
    !!games?.length && (
      <>
        <SectionBlocks>
          <Box $marginLeft={[0, '18px']}>
            <HeadingBoldH5>{displayedTitle}</HeadingBoldH5>
          </Box>
          {viewCount && (
            <Link href={viewAllUrl} legacyBehavior>
              <SectionViewAll $count={count}>
                {t('common.filter.view_all')}
              </SectionViewAll>
            </Link>
          )}
        </SectionBlocks>

        <Section $hasScroll={hasScroll} $rows={rows}>
          {!mobileLandscape &&
            games
              .slice(0, mobileLandscape ? rows * 6 : rows * 6)
              .map(renderGame)}

          <Condition match={mobileLandscape}>
            <Sections>
              {chunkedGames.map((section, sectionIndex) => (
                <SubSection
                  key={`section-${sectionIndex}`}
                  $rows={rows}
                  $hasScroll={hasScroll}
                >
                  {section
                    .slice(0, 20)
                    .map((game, index) =>
                      renderGame(game, index, sectionIndex)
                    )}
                </SubSection>
              ))}
            </Sections>
          </Condition>
        </Section>
      </>
    )
  );
};
